import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { UNITS, formatAED } from "../../data/project";

export default function Collection() {
  const [active, setActive] = useState(UNITS[0].id);
  const activeUnit = UNITS.find((u) => u.id === active);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="collection"
      data-testid="collection-section"
      className="relative bg-[#1A3626] text-[#F4F2EC] py-24 md:py-36 overflow-hidden"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-8 items-end mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-8">
            <div className="overline mb-5" style={{ color: "#B3906A" }}>
              02 / The Collection
            </div>
            <h2
              className="font-serif leading-[1.02] tracking-tight"
              style={{ fontSize: "clamp(40px, 5.5vw, 78px)" }}
            >
              Six typologies.
              <br />
              <span className="italic font-light">One island.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4">
            <p className="text-[#F4F2EC]/70 font-light leading-relaxed">
              From a 204&nbsp;sqm 3-bedroom townhome to an 1,150&nbsp;sqm Golf
              Mansion. Every typology fronts either fairway, water channel or
              landscaped park.
            </p>
          </div>
        </div>

        {/* Active unit spotlight */}
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-12">
          <motion.div
            key={activeUnit.id + "-img"}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7 }}
            className="col-span-12 md:col-span-7 aspect-[16/11] overflow-hidden"
          >
            <img
              src={activeUnit.image}
              alt={activeUnit.name}
              className="w-full h-full object-cover"
            />
          </motion.div>

          <motion.div
            key={activeUnit.id + "-data"}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="col-span-12 md:col-span-5 flex flex-col"
          >
            <div
              className="overline"
              style={{ color: "#B3906A" }}
              data-testid="unit-detail-name-label"
            >
              {activeUnit.name}
            </div>
            <h3
              className="mt-3 font-serif leading-[1] tracking-tight"
              style={{ fontSize: "clamp(36px, 4.5vw, 64px)" }}
              data-testid="unit-detail-price"
            >
              {activeUnit.priceLabel}
            </h3>
            <p className="mt-4 text-[#F4F2EC]/75 font-light text-lg leading-relaxed">
              {activeUnit.tagline}
            </p>

            <div className="mt-8 grid grid-cols-2 gap-y-5 gap-x-6 pt-6 border-t border-[#F4F2EC]/15">
              <Spec label="Built-up area" value={`${activeUnit.sqm} sqm`} />
              <Spec label="EOI Booking Fee" value={formatAED(activeUnit.eoi)} />
              <Spec label="Down payment (5%)" value={formatAED(activeUnit.downpayment)} />
              <Spec label="Handover (60%)" value={formatAED(activeUnit.handoverPayment)} />
              <Spec
                label="Est. value at handover"
                value={formatAED(activeUnit.estValueHandover)}
                accent
              />
              <Spec
                label="Return on equity"
                value={`${activeUnit.roiOnEquity}%`}
                accent
              />
            </div>

            <button
              data-testid="unit-detail-cta"
              onClick={() => scrollTo("contact")}
              className="hge-btn-light mt-10 w-fit"
            >
              Enquire on {activeUnit.name}
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>

        {/* Typology selector */}
        <div className="border-t border-[#F4F2EC]/15 pt-8">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#F4F2EC]/55 mb-6">
            Browse all typologies
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[#F4F2EC]/10">
            {UNITS.map((u) => (
              <button
                key={u.id}
                data-testid={`unit-tab-${u.id}`}
                onClick={() => setActive(u.id)}
                className={`bg-[#1A3626] text-left px-5 py-6 transition-all duration-300 ${
                  active === u.id
                    ? "bg-[#234734]"
                    : "hover:bg-[#1f3f2d]"
                }`}
              >
                <div
                  className={`text-[10px] tracking-[0.24em] uppercase ${
                    active === u.id ? "text-[#B3906A]" : "text-[#F4F2EC]/55"
                  }`}
                >
                  {u.sqm} sqm
                </div>
                <div className="mt-2 font-serif text-lg leading-tight">
                  {u.name}
                </div>
                <div className="mt-3 text-[11px] tracking-[0.18em] uppercase text-[#F4F2EC]/65">
                  {u.priceLabel}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Spec({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.24em] uppercase text-[#F4F2EC]/55">
        {label}
      </div>
      <div
        className={`mt-1.5 font-serif text-xl md:text-2xl ${
          accent ? "text-[#B3906A]" : "text-[#F4F2EC]"
        }`}
      >
        {value}
      </div>
    </div>
  );
}
